'use client';

import { useState, useEffect } from 'react';
import {
  ControlType,
  CalculatorId,
  CalculatorType,
  DataTableInputMode,
  type CalculatorConfig,
  type ControlValues,
  type SelectControl,
  type NumberControl
} from '../../../lib/calculators/calculator-config';
import { calculateDegreesOfFreedom } from '../../../lib/stats/degrees-of-freedom';
import { DegreesOfFreedom } from './degrees-of-freedom-calculator';

export interface DegreesOfFreedomControlValues extends ControlValues {
  dataType: string;
  sampleSize?: number;
  numberOfGroups?: number;
  numberOfLevels?: number;
  rows?: number;
  columns?: number;
}

export const degreesOfFreedomConfig: CalculatorConfig<CalculatorId.DEGREES_OF_FREEDOM> = {
  id: CalculatorId.DEGREES_OF_FREEDOM,
  type: CalculatorType.Table,
  name: 'Degrees of Freedom Calculator',
  dataTableInputMode: DataTableInputMode.None,
  controls: {
    tabs: [
      {
        title: 'Parameters',
        controls: [
          {
            id: 'dataType',
            type: ControlType.Select,
            label: 'Statistical Test Type',
            required: true,
            options: [
              { value: 'sample', label: 'Sample (t-test, CI)' },
              { value: 'population', label: 'Population' },
              { value: 'anova-between', label: 'ANOVA (Between Groups)' },
              { value: 'anova-within', label: 'ANOVA (Within Groups)' },
              { value: 'chi-square', label: 'Chi-Square Test' }
            ],
            defaultValue: 'sample'
          } as SelectControl,
          {
            id: 'sampleSize',
            type: ControlType.Number,
            label: 'Sample/Population Size (n) [Required for: Sample, Population, ANOVA Within]',
            required: false,
            min: 1,
            step: 1,
            defaultValue: 30
          } as NumberControl,
          {
            id: 'numberOfGroups',
            type: ControlType.Number,
            label: 'Number of Groups (k) [Required for: ANOVA Between, ANOVA Within]',
            required: false,
            min: 2,
            step: 1,
            defaultValue: 3
          } as NumberControl,
          {
            id: 'rows',
            type: ControlType.Number,
            label: 'Number of Rows [Required for: Chi-Square]',
            required: false,
            min: 2,
            step: 1,
            defaultValue: 2
          } as NumberControl,
          {
            id: 'columns',
            type: ControlType.Number,
            label: 'Number of Columns [Required for: Chi-Square]',
            required: false,
            min: 2,
            step: 1,
            defaultValue: 2
          } as NumberControl
        ]
      }
    ]
  },
  initialControlValues: {
    dataType: 'sample',
    sampleSize: 30,
    numberOfGroups: 3,
    rows: 2,
    columns: 2
  },
  getInitialConfig: () => ({
    dataType: 'sample',
    sampleSize: 30,
    numberOfGroups: 3,
    rows: 2,
    columns: 2
  }),
  getVisualizationData: (params) => {
    const controls = params.controls as DegreesOfFreedomControlValues;
    const { dataType, sampleSize, numberOfGroups, numberOfLevels, rows, columns } = controls;
    
    try {
      return calculateDegreesOfFreedom({
        dataType: dataType as 'sample' | 'population' | 'anova-between' | 'anova-within' | 'chi-square',
        sampleSize: typeof sampleSize === 'number' ? sampleSize : undefined,
        numberOfGroups: typeof numberOfGroups === 'number' ? numberOfGroups : undefined,
        numberOfLevels: typeof numberOfLevels === 'number' ? numberOfLevels : undefined,
        rows: typeof rows === 'number' ? rows : undefined,
        columns: typeof columns === 'number' ? columns : undefined
      });
    } catch (error) {
      console.error('Error calculating degrees of freedom:', error);
      return null;
    }
  },
  render: ({ visualizationData }) => <DegreesOfFreedom visualizationData={visualizationData} />
}; 